import React, { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import LocationInput from './LocationInput'
import { calculateDistance, getZipCoordinates, DistanceCalculationProfiler, Coordinates } from '../utils/distanceCalculator'
import { useAuth } from '../contexts/AuthContext'
import { syncSubscription, getStripe, createCheckoutSession } from '../utils/stripe'

interface Car {
  Year: number
  Model: string
  Miles: string
  Color: {
    Exterior: string
    Interior: string
  }
  Options: string[]
  Dealer: string
  Location: {
    City: string
    ZipCode: string
  }
  VIN: string
  Price: number
  Image: string
  Transmission: string
}

type SortField = 'price' | 'miles' | 'transmission' | 'year' | 'distance' | 'none'
type SortDirection = 'asc' | 'desc'
type TransmissionFilter = 'all' | 'manual' | 'auto'

interface ZoomedImage {
  src: string
  alt: string
}

interface HomeProps {
  onSignInClick?: () => void
}

// Placeholder data for unauthenticated users
const PLACEHOLDER_CARS: Car[] = [
  {
    Year: 2023,
    Model: "M2",
    Miles: "17,347",
    Color: {
      Exterior: "Red",
      Interior: "Black"
    },
    Options: [],
    Dealer: "West State Motorsport",
    Location: {
      City: "Federal Way, WA",
      ZipCode: "98003"
    },
    VIN: "3MF13DM04P8D40729",
    Price: 78000,
    Image: "/assets/cars/m2-red.jpeg",
    Transmission: "8-Speed Automatic"
  },
  {
    Year: 2022,
    Model: "M3",
    Miles: "5,362",
    Color: { Exterior: "Frozen Brilliant White Metallic", Interior: "Kyalami Orange/Black" },
    Options: [],
    Dealer: "Pacific BMW",
    Location: { City: "Glendale, CA", ZipCode: "91204" },
    VIN: "WBS33AY05NFL71716",
    Price: 82881,
    Image: "/assets/cars/m3-brooklyn-gray.jpeg",
    Transmission: "8-Speed Automatic"
  }
]

// Additional fake listings for blur effect (for unauthenticated users)
const FAKE_BLURRED_CARS: Car[] = [
  {
    Year: 2023,
    Model: "M3",
    Miles: "10,442",
    Color: { Exterior: "Tanzanite Blue II Metallic", Interior: "Special Order Upholstery" },
    Options: [],
    Dealer: "Corwin Buick GMC",
    Location: { City: "Reno, NV", ZipCode: "89502" },
    VIN: "WBS43AY01PFN42520",
    Price: 88995,
    Image: "/assets/cars/m3-tanzanite-blue.jpeg",
    Transmission: "8-Speed Automatic"
  },
  {
    Year: 2024,
    Model: "M2",
    Miles: "4,679",
    Color: { Exterior: "Black Sapphire Metallic", Interior: "Black W/M Color Highlight" },
    Options: [],
    Dealer: "Ed Morse Sawgrass Auto Mall",
    Location: { City: "Fort Lauderdale, FL", ZipCode: "33323" },
    VIN: "3MF13DM04R8E77401",
    Price: 65395,
    Image: "/assets/cars/m2-black-sapphire.jpeg",
    Transmission: "6-Speed Manual"
  },
  {
    Year: 2023,
    Model: "M4",
    Miles: "14,920",
    Color: { Exterior: "Sao Paulo Yellow", Interior: "Black" },
    Options: [],
    Dealer: "Towbin Dodge RAM",
    Location: { City: "Henderson, NV", ZipCode: "89014" },
    VIN: "WBS33AZ0XPCM21366",
    Price: 76990,
    Image: "/assets/cars/m4-sao-paulo-yellow.jpeg",
    Transmission: "8-Speed Automatic"
  },
  {
    Year: 2024,
    Model: "M3",
    Miles: "2,100",
    Color: { Exterior: "Isle of Man Green", Interior: "Silverstone/Black" },
    Options: [],
    Dealer: "BMW of Manhattan",
    Location: { City: "New York, NY", ZipCode: "10019" },
    VIN: "WBS43AZ09RCJ98712",
    Price: 94500,
    Image: "",
    Transmission: "6-Speed Manual"
  },
  {
    Year: 2023,
    Model: "M4",
    Miles: "8,210",
    Color: { Exterior: "Frozen Portimao Blue", Interior: "Yas Marina Blue/Black" },
    Options: [],
    Dealer: "BMW of Austin",
    Location: { City: "Austin, TX", ZipCode: "78701" },
    VIN: "WBS43AZ07PCL55103",
    Price: 91200,
    Image: "",
    Transmission: "8-Speed Automatic"
  }
]

const Home: React.FC<HomeProps> = ({ onSignInClick = () => {} }) => {
  const { user, isAuthenticated, loading: authLoading, refreshUser } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [cars, setCars] = useState<Car[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<string | null>(null)
  const [streamingInProgress, setStreamingInProgress] = useState(false)
  const [sortField, setSortField] = useState<SortField>('none')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [zoomedImage, setZoomedImage] = useState<ZoomedImage | null>(null)
  const [userLocation, setUserLocation] = useState<Coordinates | null>(null)
  const [carDistances, setCarDistances] = useState<Map<string, number>>(new Map())
  const [distanceCalculating, setDistanceCalculating] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [transmissionFilter, setTransmissionFilter] = useState<TransmissionFilter>('all')
  const [minPrice, setMinPrice] = useState<string>('')
  const [maxPrice, setMaxPrice] = useState<string>('')
  const [selectedModels, setSelectedModels] = useState<Set<string>>(new Set())
  const [minYear, setMinYear] = useState<string>('')
  const [maxYear, setMaxYear] = useState<string>('')
  const [selectedColors, setSelectedColors] = useState<Set<string>>(new Set())
  const [minMiles, setMinMiles] = useState<string>('')
  const [maxMiles, setMaxMiles] = useState<string>('')
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)

  // Public launch: every visitor receives the complete inventory experience.
  const hasFullAccess = true

  // Compute available filter options from loaded cars
  const availableModels = useMemo(() => {
    const models = new Set(cars.map(c => c.Model).filter(Boolean))
    return Array.from(models).sort()
  }, [cars])

  const availableYears = useMemo(() => {
    const years = cars.map(c => c.Year).filter(y => y > 0)
    return { min: Math.min(...years, 1990), max: Math.max(...years, 2026) }
  }, [cars])

  const availableColors = useMemo(() => {
    const colorMap = new Map<string, string>()
    cars.forEach(c => {
      const raw = c.Color?.Exterior?.trim()
      if (raw) {
        const normalized = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase()
        if (!colorMap.has(normalized.toLowerCase())) {
          colorMap.set(normalized.toLowerCase(), raw)
        }
      }
    })
    return Array.from(new Set(colorMap.values())).sort()
  }, [cars])

  // Handle subscription success from Stripe redirect
  useEffect(() => {
    const handleSubscriptionSuccess = async () => {
      if (searchParams.get('subscription') === 'success' && isAuthenticated) {
        console.log('Subscription success detected, syncing with Stripe...')
        const token = localStorage.getItem('auth_token')
        if (token) {
          try {
            await syncSubscription(token)
            await refreshUser()
            console.log('Subscription synced successfully')
          } catch (err) {
            console.error('Failed to sync subscription:', err)
          }
        }
        searchParams.delete('subscription')
        setSearchParams(searchParams)
      }
    }
    handleSubscriptionSuccess()
  }, [searchParams, isAuthenticated, refreshUser, setSearchParams])

  useEffect(() => {
    let eventSource: EventSource | null = null
    let isCleanedUp = false

    setRetryCount(0)

    // Show placeholder data for non-subscribed users
    if (!hasFullAccess) {
      setCars([...PLACEHOLDER_CARS, ...FAKE_BLURRED_CARS])
      setLoading(false)
      setStreamingInProgress(false)
      setError(null)
      return
    }

    const fetchCars = () => {
      if (isCleanedUp) return null

      setCars([])
      setLoading(true)
      setStreamingInProgress(true)
      setError(null)

      const defaultApiUrl = window.location.hostname === 'localhost'
        ? 'http://localhost:8083'
        : 'https://bmwfinder-api-25f5f05731c4.herokuapp.com'
      const apiUrl = process.env.REACT_APP_API_URL || defaultApiUrl

      const token = localStorage.getItem('auth_token')
      const streamUrl = token
        ? `${apiUrl}/api/stream/cars?token=${encodeURIComponent(token)}`
        : `${apiUrl}/api/stream/cars`

      const eventSource = new EventSource(streamUrl)
      const carBuffer: Car[] = []
      let bufferTimeout: NodeJS.Timeout | null = null

      const flushCarBuffer = () => {
        if (carBuffer.length > 0) {
          const carsToAdd = [...carBuffer]
          carBuffer.length = 0

          setCars(prevCars => {
            if (prevCars.length === 0) {
              return carsToAdd
            }

            const existingVINs = new Set(prevCars.map(car => car.VIN))
            const newCars = carsToAdd.filter(car => car && car.VIN && !existingVINs.has(car.VIN))

            return [...prevCars, ...newCars]
          })
        }
      }

      eventSource.onopen = () => {
        setRetryCount(0)
      }

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)

          switch (data.type) {
            case 'status':
              break

            case 'update_time':
              setLastUpdated(data.updated_at)
              break

            case 'car':
              carBuffer.push(data.car)

              if (bufferTimeout) {
                clearTimeout(bufferTimeout)
              }

              bufferTimeout = setTimeout(flushCarBuffer, 50)
              break

            case 'initial_load_complete':
              flushCarBuffer()
              setLoading(false)
              setStreamingInProgress(false)
              break

            case 'complete':
              flushCarBuffer()
              setLoading(false)
              setStreamingInProgress(false)
              eventSource.close()

              setTimeout(() => {
                if (!isCleanedUp) {
                  fetchCars()
                }
              }, 24 * 60 * 60 * 1000)
              break
          }
        } catch (err) {
          // Error parsing event data
        }
      }

      eventSource.onerror = () => {
        eventSource.close()

        setRetryCount(prev => {
          const newCount = prev + 1

          if (cars.length === 0 && newCount === 1) {
            setLoading(true)
          } else {
            setLoading(false)
            setStreamingInProgress(false)
          }

          const retryDelay = Math.min(3000 * Math.pow(2, prev), 12000)
          setTimeout(() => {
            if (!isCleanedUp) {
              fetchCars()
            }
          }, retryDelay)

          return newCount
        })
      }

      return eventSource
    }

    eventSource = fetchCars()

    const interval = setInterval(() => {
      if (isCleanedUp) return
      if (eventSource) {
        eventSource.close()
      }
      eventSource = fetchCars()
    }, 24 * 60 * 60 * 1000)

    return () => {
      isCleanedUp = true
      clearInterval(interval)
      if (eventSource) {
        eventSource.close()
      }
    }
  }, [isAuthenticated, authLoading, user?.has_subscription])

  // Recalculate distances when cars change and user has a location
  useEffect(() => {
    if (userLocation && cars.length > 0) {
      handleLocationChange(userLocation, 'recalculate')
    }
  }, [cars, userLocation])

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price)
  }

  const formatLastUpdated = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    }) + ' ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const getBadgeText = (car: Car) => {
    if ((car.Model === 'M3' || car.Model === 'M4') && car.Year <= 2020) {
      return 'Competition Package'
    }
    return 'Carbon Buckets'
  }

  const shortenDealerName = (dealerName: string) => {
    return dealerName
      .replace(/Certified Pre-Owned/gi, 'CPO')
      .replace(/Pre-Owned/gi, 'Pre-Own')
      .replace(/Automotive/gi, 'Auto')
      .replace(/Corporation/gi, 'Corp')
      .replace(/International/gi, 'Intl')
  }

  const parseMiles = (miles: string): number => {
    return parseInt(miles.replace(/,/g, ''), 10)
  }

  const getActualTransmission = (car: Car): string => {
    const hasCompPkg = car.Options?.some(opt =>
      opt.toLowerCase().includes('competition') ||
      opt.toLowerCase().includes('comp pkg') ||
      opt.toLowerCase().includes('comp package')
    )
    const isF8X = car.Year >= 2015 && car.Year <= 2020
    const isM3orM4 = car.Model === 'M3' || car.Model === 'M4'

    if (isF8X && isM3orM4 && hasCompPkg) {
      return 'Manual'
    }

    return car.Transmission
  }

  const getSortedCars = (): Car[] => {
    let filteredCars = [...cars]

    // Model filter
    if (selectedModels.size > 0) {
      filteredCars = filteredCars.filter(car => selectedModels.has(car.Model))
    }

    // Year filter
    if (minYear || maxYear) {
      const minYearNum = minYear ? parseInt(minYear) : 0
      const maxYearNum = maxYear ? parseInt(maxYear) : Infinity
      filteredCars = filteredCars.filter(car => car.Year >= minYearNum && car.Year <= maxYearNum)
    }

    // Color filter
    if (selectedColors.size > 0) {
      filteredCars = filteredCars.filter(car => {
        const ext = car.Color?.Exterior?.toLowerCase() || ''
        return Array.from(selectedColors).some(c => ext.includes(c.toLowerCase()))
      })
    }

    // Transmission filter
    if (transmissionFilter !== 'all') {
      filteredCars = filteredCars.filter(car => {
        const actualTransmission = getActualTransmission(car)
        if (!actualTransmission) return false
        const transmissionLower = actualTransmission.toLowerCase()
        if (transmissionFilter === 'manual') {
          return transmissionLower.includes('manual') && !transmissionLower.includes('auto')
        } else if (transmissionFilter === 'auto') {
          return transmissionLower.includes('auto') && !transmissionLower.includes('manual')
        }
        return true
      })
    }

    // Price filter
    const minPriceNum = minPrice ? parseFloat(minPrice) : 0
    const maxPriceNum = maxPrice ? parseFloat(maxPrice) : Infinity

    if (minPrice || maxPrice) {
      filteredCars = filteredCars.filter(car => {
        const price = car.Price === 0 ? Infinity : car.Price
        return price >= minPriceNum && price <= maxPriceNum
      })
    }

    // Mileage filter
    if (minMiles || maxMiles) {
      const minMilesNum = minMiles ? parseInt(minMiles) : 0
      const maxMilesNum = maxMiles ? parseInt(maxMiles) : Infinity
      filteredCars = filteredCars.filter(car => {
        const miles = parseMiles(car.Miles)
        return miles >= minMilesNum && miles <= maxMilesNum
      })
    }

    if (sortField === 'none') return filteredCars;

    const sortedCars = [...filteredCars].sort((a, b) => {
      let comparison = 0

      switch (sortField) {
        case 'price':
          const priceA = a.Price === 0 ? Infinity : a.Price
          const priceB = b.Price === 0 ? Infinity : b.Price
          comparison = priceA - priceB
          break
        case 'miles':
          comparison = parseMiles(a.Miles) - parseMiles(b.Miles)
          break
        case 'transmission':
          const transA = a.Transmission === 'Manual' ? 0 : 1
          const transB = b.Transmission === 'Manual' ? 0 : 1
          comparison = transA - transB
          break
        case 'year':
          comparison = a.Year - b.Year
          break
        case 'distance':
          const distanceA = carDistances.get(a.VIN) ?? 999999
          const distanceB = carDistances.get(b.VIN) ?? 999999
          comparison = distanceA - distanceB
          break
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })

    return sortedCars
  }

  const handleSort = (field: SortField) => {
    if (field === 'distance' && !userLocation) {
      return
    }

    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const handleLocationChange = async (coordinates: Coordinates | null, _userInput: string) => {
    setUserLocation(coordinates)

    if (!coordinates) {
      setCarDistances(new Map())
      if (sortField === 'distance') {
        setSortField('none')
      }
      return
    }

    setDistanceCalculating(true)

    try {
      const profiler = new DistanceCalculationProfiler()
      profiler.start()

      const distanceMap = new Map<string, number>()

      const distancePromises = cars.map(async (car) => {
        try {
          const carCoords = await getZipCoordinates(car.Location.ZipCode);

          if (carCoords) {
            const distance = calculateDistance(coordinates, carCoords);
            profiler.incrementCalculation();
            return { vin: car.VIN, distance };
          }

          return { vin: car.VIN, distance: null };
        } catch (error) {
          return { vin: car.VIN, distance: null };
        }
      });

      const distanceResults = await Promise.all(distancePromises);

      for (const result of distanceResults) {
        if (result.distance !== null) {
          distanceMap.set(result.vin, result.distance);
        }
      }

      setCarDistances(distanceMap)

      setSortField('distance')
      setSortDirection('asc')

    } catch (error) {
      // Error calculating distances
    } finally {
      setDistanceCalculating(false)
    }
  }

  const LoadingState = () => (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-rose-600/10 rounded-full">
          <div className="w-8 h-8 border-3 border-rose-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-white">
            {authLoading ? 'Initializing...' : loading ? 'Loading cars...' : `Streaming cars...`}
          </h3>
          <p className="text-neutral-500">
            {authLoading ? 'Setting up your session' : loading ? 'Fetching the latest inventory' : `${cars.length} cars loaded`}
          </p>
        </div>
      </div>
    </div>
  )

  const EmptyState = () => (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1a1a1a] rounded-full">
          <svg className="w-8 h-8 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-white">No cars found</h3>
          <p className="text-neutral-500">Check back soon for new inventory</p>
        </div>
      </div>
    </div>
  )

  // Lock overlay for non-authenticated / non-subscribed users
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null)

  const handleCheckout = async (tier: 'day_trial' | 'basic') => {
    if (!isAuthenticated) {
      onSignInClick()
      return
    }
    setCheckoutLoading(tier)
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) throw new Error('Please sign in')
      const session = await createCheckoutSession(token, tier, 'month')
      const stripe = await getStripe()
      if (!stripe) throw new Error('Failed to load Stripe')
      await stripe.redirectToCheckout({ sessionId: session.session_id })
    } catch (err) {
      console.error(err)
      setCheckoutLoading(null)
    }
  }

  const LockOverlay = () => (
    <div className="absolute inset-0 z-20 flex items-center justify-center">
      <div className="absolute inset-0 bg-[#0a0a0a]/70" />

      <div className="relative z-10 max-w-lg w-full mx-4 bg-[#141414] rounded-2xl border border-white/10 p-6 sm:p-8 text-center shadow-2xl">
        {/* Lock icon */}
        <div className="mx-auto w-14 h-14 rounded-full bg-rose-600/10 flex items-center justify-center mb-5">
          <svg className="w-7 h-7 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M7 11V7a5 5 0 0110 0v4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        <h3 className="text-xl sm:text-2xl font-bold text-white mb-2" style={{ letterSpacing: '-0.02em' }}>
          {isAuthenticated ? 'Unlock Full Inventory' : 'Sign in to Browse'}
        </h3>
        <p className="text-sm text-neutral-400 mb-6 leading-relaxed max-w-sm mx-auto">
          {isAuthenticated
            ? 'Get access to every VIN-decoded BMW listing with real-time updates, sorting, and filtering.'
            : 'Create a free account to preview listings, or subscribe for full inventory access.'
          }
        </p>

        {isAuthenticated ? (
          <>
            {/* Two plan cards side by side */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              {/* Day Pass */}
              <div className="bg-[#0d0d0d] rounded-xl border border-white/10 p-4 text-left">
                <span className="font-mono text-[10px] text-neutral-500 uppercase tracking-widest">Day Pass</span>
                <div className="flex items-baseline gap-0.5 mt-1 mb-1">
                  <span className="text-2xl font-bold text-white">$1</span>
                  <span className="text-2xl font-bold text-rose-500">.99</span>
                </div>
                <p className="text-[11px] text-neutral-500 mb-3">24-hour access · no renewal</p>
                <button
                  onClick={() => handleCheckout('day_trial')}
                  disabled={checkoutLoading !== null}
                  className="w-full py-2.5 rounded-lg text-sm font-semibold bg-white/10 hover:bg-white/15 text-white border border-white/10 transition-colors cursor-pointer disabled:opacity-50"
                >
                  {checkoutLoading === 'day_trial' ? 'Processing...' : 'Get Day Pass'}
                </button>
              </div>

              {/* Monthly */}
              <div className="bg-[#0d0d0d] rounded-xl border border-rose-600/30 p-4 text-left relative">
                <div className="absolute -top-2 right-3 bg-rose-600 text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">Best Value</div>
                <span className="font-mono text-[10px] text-rose-500 uppercase tracking-widest">Monthly</span>
                <div className="flex items-baseline gap-0.5 mt-1 mb-1">
                  <span className="text-2xl font-bold text-white">$4</span>
                  <span className="text-2xl font-bold text-rose-500">.99</span>
                  <span className="text-[11px] text-neutral-500 ml-0.5">/mo</span>
                </div>
                <p className="text-[11px] text-neutral-500 mb-3">Unlimited · cancel anytime</p>
                <button
                  onClick={() => handleCheckout('basic')}
                  disabled={checkoutLoading !== null}
                  className="w-full py-2.5 rounded-lg text-sm font-semibold bg-rose-600 hover:bg-rose-700 text-white border-none transition-colors cursor-pointer disabled:opacity-50"
                  style={{ boxShadow: '0 2px 8px rgba(225,29,72,0.3)' }}
                >
                  {checkoutLoading === 'basic' ? 'Processing...' : 'Get Monthly'}
                </button>
              </div>
            </div>

            {/* Trust badges */}
            <div className="flex items-center justify-center gap-4 text-[11px] text-neutral-600">
              <div className="flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Secure
              </div>
              <div className="flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Instant access
              </div>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <button
              onClick={onSignInClick}
              className="w-full inline-flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-6 py-3.5 rounded-lg text-base font-semibold transition-colors border-none cursor-pointer"
              style={{ boxShadow: '0 4px 12px rgba(225, 29, 72, 0.3)' }}
            >
              Get Started
            </button>
            <button
              onClick={onSignInClick}
              className="w-full inline-flex items-center justify-center gap-2 bg-transparent text-neutral-400 px-6 py-3 rounded-lg text-sm font-medium hover:text-white hover:bg-white/5 transition-all cursor-pointer border border-white/10"
            >
              Already have an account? Sign in
            </button>
          </div>
        )}
      </div>
    </div>
  )

  if (authLoading) {
    return (
      <main className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 pt-24 md:pt-28 pb-8 overflow-x-hidden">
        <LoadingState />
      </main>
    )
  }

  const displayCars = getSortedCars()

  return (
    <main className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 pt-24 md:pt-28 pb-8 overflow-x-hidden">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Inventory</h1>
            {lastUpdated && hasFullAccess && (
              <p className="text-sm text-neutral-400 mt-1">
                Last updated {formatLastUpdated(lastUpdated)}
              </p>
            )}
            {!hasFullAccess && (
              <p className="text-sm text-neutral-400 mt-1">
                Preview — sign in for full access
              </p>
            )}
          </div>
          {hasFullAccess && (
            <button
              onClick={() => setIsFilterModalOpen(!isFilterModalOpen)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all border-none cursor-pointer ${
                isFilterModalOpen
                  ? 'bg-white/10 text-white ring-1 ring-rose-500/50'
                  : 'bg-[#141414] border border-white/10 text-neutral-300 hover:text-white hover:bg-white/5'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters
              {(selectedModels.size > 0 || minYear || maxYear || selectedColors.size > 0 || transmissionFilter !== 'all' || minPrice || maxPrice || minMiles || maxMiles || sortField !== 'none') && (
                <span className="w-5 h-5 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center">
                  {[
                    selectedModels.size > 0,
                    minYear || maxYear,
                    selectedColors.size > 0,
                    transmissionFilter !== 'all',
                    minPrice || maxPrice,
                    minMiles || maxMiles,
                    sortField !== 'none',
                  ].filter(Boolean).length}
                </span>
              )}
            </button>
          )}
        </div>

        {/* Inline Filter Panel */}
        {hasFullAccess && isFilterModalOpen && (
          <div className="bg-[#141414] rounded-xl border border-white/10 overflow-hidden mb-2">
            {/* Sort row */}
            <div className="px-4 py-3 border-b border-white/5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mr-1 whitespace-nowrap">Sort</span>
                {[
                  { field: 'price' as SortField, label: 'Price' },
                  { field: 'year' as SortField, label: 'Year' },
                  { field: 'miles' as SortField, label: 'Mileage' },
                  { field: 'distance' as SortField, label: 'Distance', disabled: !userLocation },
                ].map(({ field, label, disabled }) => (
                  <button
                    key={field}
                    onClick={() => !disabled && handleSort(field)}
                    disabled={disabled}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all border-none cursor-pointer ${
                      disabled
                        ? 'bg-white/5 text-neutral-600 cursor-not-allowed'
                        : sortField === field
                        ? 'bg-rose-600 text-white'
                        : 'bg-white/5 text-neutral-300 hover:bg-white/10'
                    }`}
                  >
                    {label}
                    {sortField === field && (
                      <span className="ml-1">{sortDirection === 'asc' ? '\u2191' : '\u2193'}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Filter grid */}
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Model */}
              {availableModels.length > 0 && (
                <div>
                  <label className="block text-[11px] font-semibold text-neutral-500 uppercase tracking-wider mb-2">Model</label>
                  <div className="flex flex-wrap gap-1.5">
                    {availableModels.map(model => (
                      <button
                        key={model}
                        onClick={() => {
                          const next = new Set(selectedModels)
                          next.has(model) ? next.delete(model) : next.add(model)
                          setSelectedModels(next)
                        }}
                        className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all border-none cursor-pointer ${
                          selectedModels.has(model)
                            ? 'bg-rose-600 text-white'
                            : 'bg-white/5 text-neutral-300 hover:bg-white/10'
                        }`}
                      >
                        {model}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Year */}
              <div>
                <label className="block text-[11px] font-semibold text-neutral-500 uppercase tracking-wider mb-2">Year</label>
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    placeholder="From"
                    value={minYear}
                    onChange={(e) => setMinYear(e.target.value)}
                    className="flex-1 px-2.5 py-1.5 text-xs border border-white/10 rounded-md bg-white/5 text-white placeholder-neutral-600 focus:ring-1 focus:ring-rose-500 focus:border-transparent outline-none"
                  />
                  <span className="text-neutral-600 text-xs">-</span>
                  <input
                    type="number"
                    placeholder="To"
                    value={maxYear}
                    onChange={(e) => setMaxYear(e.target.value)}
                    className="flex-1 px-2.5 py-1.5 text-xs border border-white/10 rounded-md bg-white/5 text-white placeholder-neutral-600 focus:ring-1 focus:ring-rose-500 focus:border-transparent outline-none"
                  />
                </div>
              </div>

              {/* Transmission */}
              <div>
                <label className="block text-[11px] font-semibold text-neutral-500 uppercase tracking-wider mb-2">Transmission</label>
                <div className="flex gap-1.5">
                  {[
                    { value: 'all' as TransmissionFilter, label: 'All' },
                    { value: 'manual' as TransmissionFilter, label: 'Manual' },
                    { value: 'auto' as TransmissionFilter, label: 'Auto' },
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => setTransmissionFilter(value)}
                      className={`flex-1 px-2.5 py-1.5 text-xs font-medium rounded-md transition-all border-none cursor-pointer ${
                        transmissionFilter === value
                          ? 'bg-rose-600 text-white'
                          : 'bg-white/5 text-neutral-300 hover:bg-white/10'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price */}
              <div>
                <label className="block text-[11px] font-semibold text-neutral-500 uppercase tracking-wider mb-2">Price</label>
                <div className="flex gap-2 items-center">
                  <div className="flex-1 relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-neutral-600">$</span>
                    <input
                      type="number"
                      placeholder="Min"
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                      className="w-full pl-5 pr-2 py-1.5 text-xs border border-white/10 rounded-md bg-white/5 text-white placeholder-neutral-600 focus:ring-1 focus:ring-rose-500 focus:border-transparent outline-none"
                    />
                  </div>
                  <span className="text-neutral-600 text-xs">-</span>
                  <div className="flex-1 relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-neutral-600">$</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                      className="w-full pl-5 pr-2 py-1.5 text-xs border border-white/10 rounded-md bg-white/5 text-white placeholder-neutral-600 focus:ring-1 focus:ring-rose-500 focus:border-transparent outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Mileage */}
              <div>
                <label className="block text-[11px] font-semibold text-neutral-500 uppercase tracking-wider mb-2">Mileage</label>
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    placeholder="Min"
                    value={minMiles}
                    onChange={(e) => setMinMiles(e.target.value)}
                    className="flex-1 px-2.5 py-1.5 text-xs border border-white/10 rounded-md bg-white/5 text-white placeholder-neutral-600 focus:ring-1 focus:ring-rose-500 focus:border-transparent outline-none"
                  />
                  <span className="text-neutral-600 text-xs">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={maxMiles}
                    onChange={(e) => setMaxMiles(e.target.value)}
                    className="flex-1 px-2.5 py-1.5 text-xs border border-white/10 rounded-md bg-white/5 text-white placeholder-neutral-600 focus:ring-1 focus:ring-rose-500 focus:border-transparent outline-none"
                  />
                </div>
              </div>

              {/* Exterior Color */}
              <div className="sm:col-span-2 lg:col-span-1">
                <label className="block text-[11px] font-semibold text-neutral-500 uppercase tracking-wider mb-2">Color</label>
                <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                  {availableColors.map(color => (
                    <button
                      key={color}
                      onClick={() => {
                        const next = new Set(selectedColors)
                        next.has(color) ? next.delete(color) : next.add(color)
                        setSelectedColors(next)
                      }}
                      className={`px-2 py-0.5 text-[11px] rounded transition-all border-none cursor-pointer ${
                        selectedColors.has(color)
                          ? 'bg-rose-600 text-white'
                          : 'bg-white/5 text-neutral-400 hover:bg-white/10'
                      }`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Active filters + Reset */}
            {(selectedModels.size > 0 || minYear || maxYear || selectedColors.size > 0 || transmissionFilter !== 'all' || minPrice || maxPrice || minMiles || maxMiles || sortField !== 'none') && (
              <div className="px-4 py-2.5 border-t border-white/5 flex items-center justify-between">
                <div className="flex flex-wrap items-center gap-1.5">
                  {selectedModels.size > 0 && Array.from(selectedModels).map(m => (
                    <span key={m} className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-blue-500/15 text-blue-400 rounded-full">
                      {m}
                      <button onClick={() => { const n = new Set(selectedModels); n.delete(m); setSelectedModels(n) }} className="hover:text-white bg-transparent border-none cursor-pointer text-blue-400 p-0 text-[11px]">&times;</button>
                    </span>
                  ))}
                  {(minYear || maxYear) && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-emerald-500/15 text-emerald-400 rounded-full">
                      {minYear || '...'}-{maxYear || '...'}
                      <button onClick={() => { setMinYear(''); setMaxYear('') }} className="hover:text-white bg-transparent border-none cursor-pointer text-emerald-400 p-0 text-[11px]">&times;</button>
                    </span>
                  )}
                  {transmissionFilter !== 'all' && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-rose-500/15 text-rose-400 rounded-full">
                      {transmissionFilter === 'manual' ? 'Manual' : 'Auto'}
                      <button onClick={() => setTransmissionFilter('all')} className="hover:text-white bg-transparent border-none cursor-pointer text-rose-400 p-0 text-[11px]">&times;</button>
                    </span>
                  )}
                  {(minPrice || maxPrice) && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-violet-500/15 text-violet-400 rounded-full">
                      ${minPrice || '0'}-${maxPrice || '\u221E'}
                      <button onClick={() => { setMinPrice(''); setMaxPrice('') }} className="hover:text-white bg-transparent border-none cursor-pointer text-violet-400 p-0 text-[11px]">&times;</button>
                    </span>
                  )}
                  {(minMiles || maxMiles) && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-cyan-500/15 text-cyan-400 rounded-full">
                      {minMiles || '0'}-{maxMiles || '\u221E'} mi
                      <button onClick={() => { setMinMiles(''); setMaxMiles('') }} className="hover:text-white bg-transparent border-none cursor-pointer text-cyan-400 p-0 text-[11px]">&times;</button>
                    </span>
                  )}
                  {selectedColors.size > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-amber-500/15 text-amber-400 rounded-full">
                      {selectedColors.size} color{selectedColors.size > 1 ? 's' : ''}
                      <button onClick={() => setSelectedColors(new Set())} className="hover:text-white bg-transparent border-none cursor-pointer text-amber-400 p-0 text-[11px]">&times;</button>
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSortField('none')
                    setTransmissionFilter('all')
                    setMinPrice(''); setMaxPrice('')
                    setSelectedModels(new Set())
                    setMinYear(''); setMaxYear('')
                    setSelectedColors(new Set())
                    setMinMiles(''); setMaxMiles('')
                  }}
                  className="text-xs text-neutral-500 hover:text-rose-400 bg-transparent border-none cursor-pointer whitespace-nowrap ml-3"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Loading or Empty State (only for subscribed users) */}
      {hasFullAccess && (loading || streamingInProgress) && <LoadingState />}
      {hasFullAccess && !loading && !streamingInProgress && cars.length === 0 && <EmptyState />}

      {/* Location Input - Only show for Premium subscribers */}
      {hasFullAccess && user?.subscription_tier === 'premium' && (
        <div className="bg-[#141414] rounded-lg border border-white/10 p-4 sm:p-6 shadow-sm mb-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            Sort by Distance
          </h3>
          <LocationInput
            onLocationChange={handleLocationChange}
            isLoading={distanceCalculating}
          />
        </div>
      )}

      {/* Content */}
      {displayCars.length > 0 && (
        <div className="space-y-6 relative">
          {/* Results count */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-neutral-300">
              <span className="font-semibold text-white">{displayCars.length}</span> cars
            </span>
            {hasFullAccess && (loading || streamingInProgress) && (
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 bg-rose-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-rose-500">Updating</span>
              </div>
            )}
          </div>

          {/* Inventory table wrapper — relative for lock overlay positioning */}
          <div className="relative">
            {/* Lock overlay for non-subscribed users */}
            {!hasFullAccess && <LockOverlay />}

            {/* Desktop Table */}
            <div className="hidden xl:block mb-6">
              <div className={`bg-[#141414] border border-white/10 overflow-x-auto shadow-sm rounded-lg ${!hasFullAccess ? 'select-none' : ''}`}>
                <table className="w-full table-fixed min-w-[1024px]">
                  <colgroup>
                    <col style={{ width: '22%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '22%' }} />
                    <col style={{ width: '16%' }} />
                    <col style={{ width: '10%' }} />
                  </colgroup>
                  <thead className="bg-[#1a1a1a] border-b border-white/10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                        Vehicle
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                        Image
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                        Miles
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                        Trans
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                        Color
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                        Location
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                        Price
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {displayCars.map((car, idx) => (
                      <tr key={car.VIN} className={`hover:bg-white/5 transition-colors ${!hasFullAccess && idx >= 2 ? 'blur-[6px] pointer-events-none' : ''}`}>
                        <td className="px-4 py-4">
                          <div className="space-y-1">
                            <div className="font-semibold text-white">
                              {car.Year} {car.Model}
                            </div>
                            <div className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-rose-600/10 text-rose-400 rounded-full">
                              ✓ {getBadgeText(car)}
                            </div>
                            <div className="text-xs text-neutral-500 font-mono">
                              {car.VIN}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          {car.Image ? (
                            <img
                              src={car.Image}
                              alt={`${car.Year} ${car.Model}`}
                              className="w-20 h-14 object-cover rounded-lg border border-white/10 cursor-pointer hover:opacity-80 transition-opacity shadow-sm"
                              onClick={() => hasFullAccess && setZoomedImage({ src: car.Image, alt: `${car.Year} ${car.Model}` })}
                              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                e.currentTarget.src = '';
                              }}
                            />
                          ) : (
                            <div className="w-20 h-14 bg-[#1a1a1a] rounded-lg border border-white/10 flex items-center justify-center">
                              <svg className="w-6 h-6 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-white">
                            {car.Miles}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-white">
                            {getActualTransmission(car).toLowerCase().includes('manual') ? 'Manual' : 'Auto'}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="space-y-1">
                            <div className="text-sm font-medium text-white">{car.Color.Exterior}</div>
                            <div className="text-xs text-neutral-500">{car.Color.Interior}</div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="space-y-1">
                            <div className="text-sm font-medium text-white">{car.Location.City}</div>
                            <div className="text-xs text-neutral-500">{car.Location.ZipCode}</div>
                            {carDistances.has(car.VIN) && (
                              <div className="text-xs text-rose-500">
                                {carDistances.get(car.VIN)!.toFixed(0)} miles away
                              </div>
                            )}
                            <div className="text-xs text-neutral-500 truncate max-w-[8rem]" title={car.Dealer}>
                              {shortenDealerName(car.Dealer)}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-lg font-bold text-white">
                            {formatPrice(car.Price)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Cards */}
            <div className="xl:hidden space-y-4">
              {displayCars.map((car, idx) => (
                <div key={car.VIN} className={`bg-[#141414] rounded-lg border border-white/10 p-6 shadow-sm hover:shadow-md transition-shadow ${!hasFullAccess && idx >= 2 ? 'blur-[6px] pointer-events-none' : ''}`}>
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1 min-w-0 mr-4">
                      <h3 className="text-lg font-bold text-white">
                        {car.Year} {car.Model}
                      </h3>
                      <div className="inline-flex items-center px-2 py-0.5 mt-1 text-xs font-medium bg-rose-600/10 text-rose-400 rounded-full">
                        ✓ {getBadgeText(car)}
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      <div className="text-xl font-bold text-white">
                        {formatPrice(car.Price)}
                      </div>
                      {car.Image && (
                        <img
                          src={car.Image}
                          alt={`${car.Year} ${car.Model}`}
                          className="w-24 h-16 object-cover rounded-lg border border-white/10 cursor-pointer hover:opacity-80 transition-opacity shadow-sm"
                          onClick={() => hasFullAccess && setZoomedImage({ src: car.Image, alt: `${car.Year} ${car.Model}` })}
                          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                            e.currentTarget.src = '';
                          }}
                        />
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-neutral-500 block mb-1">Miles</span>
                      <div className="font-semibold text-white">{car.Miles}</div>
                    </div>
                    <div>
                      <span className="text-neutral-500 block mb-1">Transmission</span>
                      <div className="font-semibold text-white">{getActualTransmission(car) || 'Unknown'}</div>
                    </div>
                    <div>
                      <span className="text-neutral-500 block mb-1">Location</span>
                      <div className="font-semibold text-white">{car.Location.City}</div>
                      <div className="text-xs text-neutral-500">{car.Location.ZipCode}</div>
                    </div>
                    <div>
                      <span className="text-neutral-500 block mb-1">Colors</span>
                      <div className="font-semibold text-white">{car.Color.Exterior}</div>
                      <div className="text-xs text-neutral-500">{car.Color.Interior}</div>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-white/5">
                    <div className="text-sm">
                      <span className="text-neutral-500 block mb-1">Dealer</span>
                      <div className="font-medium text-white">{shortenDealerName(car.Dealer)}</div>
                    </div>
                    <div className="mt-2 text-xs">
                      <span className="text-neutral-500 block mb-1">VIN</span>
                      <div className="font-mono text-neutral-400">{car.VIN}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setZoomedImage(null)}
        >
          <div className="relative w-full h-full max-w-7xl max-h-full flex items-center justify-center">
            <button
              onClick={() => setZoomedImage(null)}
              className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/75 transition-all z-10"
              aria-label="Close image"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={zoomedImage.src}
              alt={zoomedImage.alt}
              className="max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] object-contain rounded-lg"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </main>
  )
}

export default Home
